/**
 * Versioned HTTP contract — `v1` — for the MODEL CATALOG (`POST /v1/models/list`,
 * IDEA-48 / ADR 0054).
 *
 * The gateway publishes which providers/models an operator may select, with pinned
 * prices and thinking capability per model — the allowlist made browsable. The web app
 * renders the picker from this response and NEVER hard-codes models; the gateway
 * validates every returned selection fail-closed against the same catalog, so this
 * endpoint can only inform the UI, never widen what the server accepts.
 */
import { z } from 'zod';
import { apiEnvelope, EffortLevelSchema } from './v1.js';
import { LLM_PROVIDERS } from '../llm-models.js';

/** One selectable model as the server catalog describes it. */
export const ModelOptionSchema = z.object({
  provider: z.enum(LLM_PROVIDERS),
  id: z.string().min(1).max(120),
  label: z.string().min(1).max(120),
  /** Pinned $/1M-token rates (mirrors the gateway price table — informational). */
  inPerMtok: z.number().nonnegative(),
  outPerMtok: z.number().nonnegative(),
  thinking: z.object({
    supported: z.boolean(),
    levels: z.array(EffortLevelSchema).max(8),
  }),
});
export type ModelOptionPayload = z.infer<typeof ModelOptionSchema>;

/** The full catalog: providers → their models, plus known-but-unconfigured providers
 *  (`disabled`, IDEA-49) so diagnostics can show provider posture. Additive + optional —
 *  payloads from an ADR-0054-era gateway still parse. */
export const ProviderCatalogSchema = z.object({
  providers: z.array(
    z.object({
      id: z.enum(LLM_PROVIDERS),
      label: z.string().min(1).max(120),
      models: z.array(ModelOptionSchema).max(50),
    }),
  ),
  disabled: z
    .array(
      z.object({
        id: z.enum(LLM_PROVIDERS),
        label: z.string().min(1).max(120),
        reason: z.string().min(1).max(200),
      }),
    )
    .max(24)
    .optional(),
});
export type ProviderCatalogPayload = z.infer<typeof ProviderCatalogSchema>;

/** Request body for `POST /v1/models/list` — no parameters today (kept a POST for the
 *  RPC-style consistency of `/v1/*`; an owner/feature filter can ride later). */
export const ListModelsRequestSchema = z.object({});
export type ListModelsRequest = z.infer<typeof ListModelsRequestSchema>;

/** Full `POST /v1/models/list` response envelope. */
export const ListModelsResponseSchema = apiEnvelope(ProviderCatalogSchema);
export type ListModelsResponse = z.infer<typeof ListModelsResponseSchema>;
